import { router } from "@inertiajs/react"
import { useEffect } from "react"

/**
 * OKS-36 — Visibility-aware polling for the bell-badge unread count.
 *
 * Mounts a `setInterval` + `visibilitychange` listener that fires
 * `router.reload({ only: ["unread_notifications_count"] })` every
 * `intervalMs`. The interval keeps firing while the tab is hidden;
 * `refresh()` returns early on a hidden tab, so no request is made
 * (TRD §8 Risks — polling thrash). Coming back to foreground fires an
 * immediate refresh so a returning user sees the latest count without
 * waiting up to 30s.
 *
 * ## What `only:` does and does not do (OKS-1013)
 *
 * `only:` selects which props are SERIALISED. An unrequested lambda /
 * `optional` / `defer` prop is never called, so the shared
 * `unread_notifications_count` lambda is the only prop value this reload
 * computes.
 *
 * But the controller action for whatever page the tab is parked on
 * **always runs to completion** to build its props hash — `only:` filters
 * afterwards and cannot reach back into the action body. So a cheap shared
 * prop says nothing about what the page's own action costs.
 *
 * Pages whose props are plain values therefore pay their full action on
 * every tick. These actions lambda-wrap their page props, so this reload
 * skips them (their guards — `authorize!`, record loads, redirects — still
 * run):
 *   - `Brand::ShipmentsController`, `CrewMembershipsController` (OKS-1013)
 *   - `CrewAnalyticsController`, `RedemptionsController`,
 *     `RewardsController`, `BadgesController`, `MissionAnalyticsController`,
 *     `BrandHomeController`, `MissionsController`, `CandidatesController`
 *     (OKS-1016)
 *   - creator `CreatorProfileController`, `CreatorMissionsController`,
 *     `CreatorBadgesController`, `CreatorRewardsController`,
 *     `CreatorFeedController`, `CreatorLeaderboardController`,
 *     `CreatorMissionDetailsController`, `CreatorMasterclassesController#show`,
 *     `CreatorMasterclassModulesController#show`,
 *     `CreatorSubmissionsController#eligible_media`,
 *     `CreatorConversationsController`, `Creator::WelcomeBacksController`,
 *     and brand `CreatorAnalyticsController`,
 *     `CrewProfilePreviewsController` (OKS-1029)
 *   - `NotificationsController` (already wrapped)
 *
 * On creator `/messages` this tick no longer marks the thread read: only a
 * request that can show messages does (a full visit, or the page's own
 * `usePollOnFocus` thread poll), and that request resolves
 * `unread_messages_count` after the mark (OKS-1029).
 *
 * **Every other brand / agency / creator action still runs its full body on
 * each tick.** Actions measured under +6 SELECTs per tick over the auth floor
 * are left unwrapped by decision (OKS-1016, OKS-1029).
 *
 * Mount once at the shell level — multiple mounts would multiply the
 * interval frequency for no benefit. Returns nothing; cleanup is wired
 * via `useEffect`'s teardown.
 *
 * @example
 *   // In BrandShell / CreatorShell / AgencyShell:
 *   usePollUnreadCount() // 30s default
 */
export const DEFAULT_POLL_INTERVAL_MS = 30_000

export function usePollUnreadCount(
  intervalMs: number = DEFAULT_POLL_INTERVAL_MS,
) {
  useEffect(() => {
    function refresh() {
      // Only fire when the tab is foregrounded — hidden tabs shouldn't
      // burn server cycles polling a count nobody can see.
      if (
        typeof document !== "undefined" &&
        document.visibilityState !== "visible"
      ) {
        return
      }
      router.reload({
        only: ["unread_notifications_count"],
        preserveUrl: true,
      })
    }

    const interval = window.setInterval(refresh, intervalMs)

    function onVisibilityChange() {
      // Coming back to foreground: refresh immediately so the user
      // sees the latest count without waiting for the next tick.
      if (document.visibilityState === "visible") {
        refresh()
      }
    }

    document.addEventListener("visibilitychange", onVisibilityChange)

    return () => {
      window.clearInterval(interval)
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [intervalMs])
}
